/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { MENU_HEIGHT, zIndex } from 'src/components/css'
import {
  PLUGIN_BUILDER_STACK_BELOW,
  PLUGIN_SETTINGS_COLUMN_WIDTH,
} from '../constants'

interface PluginBuilderLayoutProps {
  /** The settings column: scrolls on its own beside the content. */
  settings: ReactNode
  /** The preview and embed code, stacked. */
  content: ReactNode
  /** Lifts both columns over the whole window, each scrolling on its own. */
  isFullscreen?: boolean
}

/**
 * The snippet builder's two columns, playground style: settings on the left,
 * preview and code on the right. One column on narrow screens.
 *
 * Shell and grid are present in both modes: full screen only swaps classes, so
 * the form, the preview iframe and the open setting groups never remount.
 */
export const PluginBuilderLayout = ({
  settings,
  content,
  isFullscreen = false,
}: PluginBuilderLayoutProps) => (
  <div css={isFullscreen ? fullscreenShellStyle : undefined}>
    <div css={[gridStyle, isFullscreen && fullscreenGridStyle]}>
      <div
        css={[
          settingsColumnStyle,
          isFullscreen && fullscreenSettingsColumnStyle,
        ]}
      >
        {settings}
      </div>
      <div css={[contentColumnStyle, isFullscreen && fullscreenColumnStyle]}>
        {content}
      </div>
    </div>
  </div>
)

/** The column keeps 16px clear of the menu above and the window's bottom. */
const STICKY_INSET = 16

const gridStyle = css`
  display: grid;
  grid-template-columns: ${PLUGIN_SETTINGS_COLUMN_WIDTH}px minmax(0, 1fr);
  align-items: start;
  gap: 24px;
  margin: 0 auto;

  @media (max-width: ${PLUGIN_BUILDER_STACK_BELOW - 1}px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }
`

/**
 * Holds the settings card at no more than the window's height: the card keeps
 * its title in place and scrolls its own body (`ApiAccessCard scrollable`), so
 * nothing slides out of view at the column's edge.
 */
const settingsColumnStyle = css`
  position: sticky;
  top: ${MENU_HEIGHT}px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-height: calc(100vh - ${MENU_HEIGHT + STICKY_INSET}px);

  @media (max-width: ${PLUGIN_BUILDER_STACK_BELOW - 1}px) {
    position: static;
    max-height: none;
  }
`

const contentColumnStyle = css`
  min-width: 0;
`

/**
 * Covers the window, nav included — the same shell as the registration page
 * and the Operations tab. `position: fixed` rather than `requestFullscreen()`,
 * so antd selects and the colour picker, which portal into `document.body`,
 * still render; it sits below the modal layer for the same reason.
 */
const fullscreenShellStyle = css`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.overlay};
  background: ${colors.SECONDARY_500};
  padding: 0 24px;
  overflow-y: auto;
`

/** Each column scrolls inside the window instead of the page. */
const fullscreenGridStyle = css`
  height: 100%;
  align-items: stretch;
  grid-template-rows: minmax(0, 1fr);

  @media (max-width: ${PLUGIN_BUILDER_STACK_BELOW - 1}px) {
    height: auto;
    grid-template-rows: none;
  }
`

/** In full screen the settings card gets the window's height, less the margin below. */
const fullscreenSettingsColumnStyle = css`
  position: static;
  max-height: none;
  padding-bottom: 24px;

  @media (max-width: ${PLUGIN_BUILDER_STACK_BELOW - 1}px) {
    padding-bottom: 0;
  }
`

const fullscreenColumnStyle = css`
  position: static;
  max-height: none;
  overflow-y: auto;
  padding-bottom: 24px;

  @media (max-width: ${PLUGIN_BUILDER_STACK_BELOW - 1}px) {
    overflow-y: visible;
  }
`
